import { useCallback, useMemo, useState } from 'react';
import { Box3, BufferGeometry, Vector3 } from 'three';

// Types
import { Entity, TwinEntityType, LabelMode } from '@repo/frontend-types';

// Context
import {
  useDataContext,
  useFilterContext,
  useModeContext,
  useEventContext,
} from '@repo/front-end-state';

// Utils
import {
  activeSceneBehaviourFor,
  resolveEntityBehaviourColor,
  stringToTwinEntityType,
} from '@repo/front-end-utils';
import { NotTracked, Tracked } from '@repo/ui';
import { Hitbox } from '../Hitbox';
import { TwinEntity } from '@repo/backend-types';
import { SelectionOutlines } from '@repo/ui';
import { Label3D } from '../Label3D';
import { determineVisibility, getMenuHovered, getSelectionCriteria } from '@repo/ui';

interface Props {
  entity: Entity;
  geometry: BufferGeometry;
  mapDiv: HTMLDivElement;
  lineage: TwinEntity[];
  labelPosition?: Vector3;
  entityType: TwinEntityType;
  labelBoundingBox: Box3;
  topLevelType: TwinEntityType;
}

const Box = ({
  entity,
  geometry,
  mapDiv,
  lineage,
  labelPosition,
  entityType,
  labelBoundingBox,
  topLevelType,
}: Props) => {
  // Access data-related functions and settings from Data Context.
  const { heroMetric, filter } = useFilterContext();
  const { appMode } = useModeContext();
  const { data } = useDataContext();
  const { currentHoveredEntity, currentHoveredEntityOn3DScene } = useEventContext();

  const [pressed, setPressed] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hover, setHover] = useState(false); // setHover is used by Hitbox, hover state managed globally

  const parent = lineage[lineage.length - 1];
  const parentType = stringToTwinEntityType(parent.type.name);
  const isTopLevelArea = parentType === topLevelType; // used special case for displaying 'top-level' boxes on load

  const { filtersExist, areaIsSelected, parentIsSelected, siblingIsSelected } =
    getSelectionCriteria(entity, parent, filter);

  const fullLineageIncludingThisEntity = [...lineage, entity];
  const menuHovered = getMenuHovered(fullLineageIncludingThisEntity, currentHoveredEntity);

  const behavioursVisible = determineVisibility(entity, {
    filtersExist,
    areaIsSelected,
    parentIsSelected,
    siblingIsSelected,
    isTopLevelArea,
  });

  const colorMode = filtersExist && !areaIsSelected && siblingIsSelected ? 'greys' : 'colors';

  // R7: when the heatmap behaviour is active for this (metric, mode), the floor
  // heatmap replaces the tinted-box fills — hide Tracked/NotTracked meshes. The
  // Hitbox, selection outlines and Label3D stay (a display change, not an
  // interaction change).
  const heatmapActive = activeSceneBehaviourFor(heroMetric, appMode) === 'heatmap';
  const fillVisible = behavioursVisible && !heatmapActive;

  // Entity → colour for the current hero metric + mode + selection state. The pipeline (tuple
  // lookup, mode-keyed id resolution, NA guard, raw-value colouring) lives in the shared, tested
  // resolveEntityBehaviourColor so Box, Label3D and (R7) the heatmap share one implementation.
  const color = useMemo(
    () =>
      resolveEntityBehaviourColor({
        entity,
        heroMetric,
        appMode,
        processed: data.processed,
        selected: colorMode === 'colors',
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on entity.id and entity.bID, not the entity object's identity
    [entity.id, entity.bID, heroMetric, appMode, data.processed, colorMode],
  );

  // Use global hover state for both outlines and labels to prevent stale local hover states
  const isGloballyHovered = currentHoveredEntityOn3DScene?.id === entity.id;
  const isGloballyMenuHovered = menuHovered;

  const showSolidOutline = areaIsSelected || pressed;
  const showDashedOutline = (isGloballyHovered || isGloballyMenuHovered) && !areaIsSelected;
  const showOutline = !(showSolidOutline || showDashedOutline);

  // Label visibility rules - prevent flickering during selection transitions:
  // 1. Show if directly selected (selected label)
  // 2. Show if globally hovered AND not selected (hover label)
  // 3. Show if menu hovered AND not selected (menu hover label)
  // 4. Keep showing if this entity was recently selected to prevent flicker
  const shouldShowLabel =
    areaIsSelected || (!areaIsSelected && (isGloballyHovered || isGloballyMenuHovered));

  const Label = useCallback(() => {
    return (
      <Label3D
        entity={entity}
        entityType={entityType}
        labelMode={areaIsSelected ? LabelMode.FULL : LabelMode.BRIEF}
        boundingBox={labelBoundingBox}
        visible={shouldShowLabel}
        isSelected={areaIsSelected}
      />
    );
  }, [entity, entityType, areaIsSelected, labelBoundingBox, shouldShowLabel]);

  return (
    <>
      <SelectionOutlines
        geometry={geometry}
        showDashedOutline={showDashedOutline}
        showSolidOutline={showSolidOutline}
      />
      <Hitbox
        entity={entity}
        geometry={geometry}
        setHover={setHover}
        setPressed={setPressed}
        mapDiv={mapDiv}
        lineage={lineage}
      />
      {entity.tracked ? (
        <Tracked
          color={color}
          geometry={geometry}
          entity={entity}
          visible={fillVisible}
          showOutline={showOutline}
        />
      ) : (
        <NotTracked geometry={geometry} visible={fillVisible} showOutline={showOutline} />
      )}
      {shouldShowLabel &&
        (labelPosition ? (
          <object3D position={labelPosition}>
            <Label />
          </object3D>
        ) : (
          <Label />
        ))}
    </>
  );
};

export { Box };
